import React, { useState } from 'react';
import {
  Alert,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
  Button,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useEmployeeOptions } from './shared/useEmployeeOptions';

const { Text, Paragraph } = Typography;

const money = (v: unknown) =>
  Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const EosbEmployeeProfilePage: React.FC = () => {
  const { employeeId: routeEmpId } = useParams<{ employeeId: string }>();
  const navigate = useNavigate();
  const { options: empOptions } = useEmployeeOptions({ employment_status: null });
  const [employeeId, setEmployeeId] = useState<string | undefined>(routeEmpId);
  const [asOf, setAsOf] = useState<Dayjs>(dayjs());

  const profileQ = useQuery({
    queryKey: [...hrmsKeys.all, 'eosb-profile', employeeId, asOf.format('YYYY-MM-DD')],
    queryFn: () =>
      hrmsService.getEosbEmployeeProfile(employeeId!, { as_of_date: asOf.format('YYYY-MM-DD') }),
    enabled: Boolean(employeeId),
  });

  const p = profileQ.data;

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Employee EOSB profile"
          subtitle="Statutory entitlement, accounting liability, and movement ledger"
          primaryActions={
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/eosb')}>
              Back
            </Button>
          }
        />
      }
    >
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Text strong>Employee</Text>
          <Select
            style={{ minWidth: 280 }}
            showSearch
            optionFilterProp="label"
            options={empOptions}
            value={employeeId}
            onChange={(v) => {
              setEmployeeId(v);
              if (v) navigate(`/hr/eosb/profile/${v}`, { replace: true });
            }}
            placeholder="Select employee"
          />
          <Text strong>As of</Text>
          <DatePicker value={asOf} onChange={(v) => v && setAsOf(v)} allowClear={false} />
        </Space>
      </Card>

      {!employeeId && <Alert type="info" showIcon message="Select an employee to view EOSB profile" />}
      {employeeId && profileQ.isLoading && <Spin />}
      {employeeId && profileQ.isError && (
        <Alert type="error" showIcon message="Failed to load EOSB profile" />
      )}

      {p && (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {p.scheme?.likely_alternative_scheme && (
            <Alert
              type="warning"
              showIcon
              message="Alternative scheme / pension flag"
              description={
                <>
                  <Paragraph style={{ marginBottom: 4 }}>{p.scheme.note}</Paragraph>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {(p.scheme.reasons || []).map((r: string) => (
                      <li key={r}>{r}</li>
                    ))}
                  </ul>
                </>
              }
            />
          )}

          <Alert
            type="info"
            showIcon
            message="Valuation disclaimer"
            description={p.policy?.valuation_disclaimer}
          />

          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Card>
                <Statistic title="Statutory entitlement (estimate)" value={money(p.statutory_entitlement)} />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card>
                <Statistic title="Accounting liability" value={money(p.accounting_liability)} />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card>
                <Statistic
                  title="Qualifying service (years)"
                  value={Number(p.service_years_qualifying || 0).toFixed(4)}
                />
              </Card>
            </Col>
          </Row>

          <Card title="Identity & policy">
            <Descriptions column={{ xs: 1, sm: 2 }} size="small">
              <Descriptions.Item label="Employee">
                {p.employee?.employee_code} — {p.employee?.full_name}
              </Descriptions.Item>
              <Descriptions.Item label="Join date">
                {p.employee?.date_of_joining
                  ? dayjs(p.employee.date_of_joining).format('DD-MMM-YYYY')
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Employment type">{p.employee?.employment_type || '—'}</Descriptions.Item>
              <Descriptions.Item label="Nationality">{p.employee?.nationality || '—'}</Descriptions.Item>
              <Descriptions.Item label="Policy pack">{p.policy?.policy_pack || '—'}</Descriptions.Item>
              <Descriptions.Item label="Jurisdiction">{p.policy?.jurisdiction || '—'}</Descriptions.Item>
              <Descriptions.Item label="Formula">{p.policy?.formula_key || '—'}</Descriptions.Item>
              <Descriptions.Item label="Valuation method">
                <Tag>{p.policy?.valuation_method || '—'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Current basic">{money(p.employee?.basic_salary)}</Descriptions.Item>
              <Descriptions.Item label="Gross service years">
                {Number(p.service_years_gross || 0).toFixed(4)}
              </Descriptions.Item>
              <Descriptions.Item label="Unpaid leave days">
                {p.unpaid_leave?.unpaid_leave_days ?? 0} ({p.unpaid_leave?.source || '—'})
              </Descriptions.Item>
              <Descriptions.Item label="Capped">{p.capped ? 'Yes' : 'No'}</Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Calculation steps">
            <Table
              size="small"
              pagination={false}
              rowKey={(r: any) => String(r.step)}
              dataSource={p.calculation_steps || []}
              columns={[
                { title: '#', dataIndex: 'step', width: 50 },
                { title: 'Step', dataIndex: 'title', width: 200 },
                { title: 'Detail', dataIndex: 'detail' },
                {
                  title: 'Amount',
                  dataIndex: 'amount',
                  width: 120,
                  render: (v) => (v != null ? money(v) : '—'),
                },
              ]}
            />
          </Card>

          <Card title="Valuation movements">
            <Table
              size="small"
              rowKey="id"
              pagination={{ pageSize: 8 }}
              dataSource={p.ledger?.valuation_movements || []}
              scroll={{ x: 900 }}
              columns={[
                { title: 'As of', dataIndex: 'as_of_date', width: 110 },
                {
                  title: 'Entitlement',
                  dataIndex: 'entitlement_amount',
                  width: 120,
                  render: (v) => money(v),
                },
                {
                  title: 'Liability',
                  dataIndex: 'accounting_liability',
                  width: 120,
                  render: (v) => money(v),
                },
                {
                  title: 'Period expense',
                  dataIndex: 'period_expense',
                  width: 120,
                  render: (v) => money(v),
                },
                {
                  title: 'Closing',
                  dataIndex: 'closing_liability',
                  width: 120,
                  render: (v) => money(v),
                },
                { title: 'JV', dataIndex: 'journal_voucher_id', width: 120, ellipsis: true },
              ]}
            />
          </Card>

          <Row gutter={16}>
            <Col xs={24} lg={12}>
              <Card title="Settlements">
                <Table
                  size="small"
                  rowKey="id"
                  pagination={false}
                  dataSource={p.ledger?.settlements || []}
                  columns={[
                    {
                      title: '#',
                      dataIndex: 'settlement_number',
                      render: (v, r: any) => (
                        <a onClick={() => navigate(`/hr/eosb/${r.id}`)}>{v || 'Open'}</a>
                      ),
                    },
                    { title: 'Exit', dataIndex: 'exit_type', width: 100 },
                    { title: 'Status', dataIndex: 'status', width: 100 },
                    {
                      title: 'Net',
                      dataIndex: 'net_payable',
                      width: 100,
                      render: (v) => money(v),
                    },
                  ]}
                />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Salary revisions">
                <Table
                  size="small"
                  rowKey="id"
                  pagination={false}
                  dataSource={p.ledger?.salary_revisions || []}
                  columns={[
                    { title: 'Effective', dataIndex: 'effective_date', width: 110 },
                    {
                      title: 'Previous',
                      dataIndex: 'previous_basic',
                      width: 100,
                      render: (v) => money(v),
                    },
                    {
                      title: 'New',
                      dataIndex: 'new_basic',
                      width: 100,
                      render: (v) => money(v),
                    },
                    { title: 'Status', dataIndex: 'status', width: 90 },
                  ]}
                />
              </Card>
            </Col>
          </Row>
        </Space>
      )}
    </WorkspaceLayout>
  );
};

export default EosbEmployeeProfilePage;
